import axios from "axios";
import { baseUrl } from ".";

export interface ThemeProps {
  siteName: any;
  hideBrokerage: boolean;
  hideLettings: boolean;
  hideLegals: boolean;
  socialLinks: never[];
  adminLastName: any;
  adminFirstName: string;
  adminPhoneNumber: string;
  _id: any;
  color1: string;
  color2: string;
  txtColor: string;
  buttonTxtColour?: string;
  buttonHoverTxtColour?: string;
  logo?: string;
  token: string;
  website: string;
  subwebsite: string;
  name: string;
  favIcon: string;
  hybrid: Boolean;
  googleAnalytics: string;
  hideQR?: Boolean;
  hideDealSourcing?: Boolean;
  hideConveyancing?: Boolean;
  hideMortgageBroker?: Boolean;
  hidePropertyManagement?: Boolean;
  hidePropertyRefurbishment?: Boolean;
  hideSellProperty?: Boolean;
  hideStampDutyReclaim?: Boolean;
  hideStartCompany?: Boolean;
  onHold?: Boolean;
  hideDigitalPlus?: Boolean;
  hideFurnishing?: Boolean;
  hideRemovals?: Boolean;
  hideSurveying?: Boolean;
  hideHousingAssociation?: Boolean;
  hideHomeInsurance?: Boolean;

  // SERVICE CONFIGURATION FIELDS
  lettingOwnService?: boolean;
  lettingCalendarLink?: string;
  lettingContactEmail?: string;
  lettingModalForm?: boolean;
  
  refurbishmentOwnService?: boolean;
  refurbishmentCalendarLink?: string;
  refurbishmentContactEmail?: string;
  refurbishmentModalForm?: boolean;
  
  brokerageOwnService?: boolean;
  brokerageCalendarLink?: string;
  brokerageContactEmail?: string;
  brokerageModalForm?: boolean;

  legalsOwnService?: boolean;
  legalsCalendarLink?: string;
  legalsContactEmail?: string;
  legalsModalForm?: boolean;
  
  // COMPANY SETUP - ONLY DECLARE ONCE!
  companySetupOwnService?: boolean;
  companySetupCalendarLink?: string;
  companySetupContactEmail?: string;
  companySetupModalForm?: boolean;

  furnishingOwnService?: boolean;
  furnishingCalendarLink?: string;
  furnishingContactEmail?: string;
  furnishingModalForm?: boolean;
  
  removalsOwnService?: boolean;
  removalsCalendarLink?: string;
  removalsContactEmail?: string;
  removalsModalForm?: boolean;

  surveyingOwnService?: boolean;
  surveyingCalendarLink?: string;
  surveyingContactEmail?: string;
  surveyingModalForm?: boolean;

  housingAssociationOwnService?: boolean;
  housingAssociationCalendarLink?: string;
  housingAssociationContactEmail?: string;
  housingAssociationModalForm?: boolean;

  homeInsuranceOwnService?: boolean;
  homeInsuranceCalendarLink?: string;
  homeInsuranceContactEmail?: string;
  homeInsuranceModalForm?: boolean;

  digitalPlusOwnService?: boolean;
  digitalPlusCalendarLink?: string;
  digitalPlusContactEmail?: string; 
  digitalPlusModalForm?: boolean;

  listingsOwnService?: boolean;
  listingsCalendarLink?: string;
  listingsContactEmail?: string;
  listingsModalForm?: boolean;

  dealSourcingOwnService?: boolean;
  dealSourcingCalendarLink?: string;
  dealSourcingContactEmail?: string;
  dealSourcingModalForm?: boolean;

  // NEW SERVICE CONFIGURATION FIELDS
  inspectionOwnService?: boolean;
  inspectionCalendarLink?: string;
  inspectionContactEmail?: string;
  inspectionModalForm?: boolean;

  lraOwnService?: boolean;
  lraCalendarLink?: string;
  lraContactEmail?: string;
  lraModalForm?: boolean;

  eicrInsideM25OwnService?: boolean;
  eicrInsideM25CalendarLink?: string;
  eicrInsideM25ContactEmail?: string;
  eicrInsideM25ModalForm?: boolean;

  eicrOutsideM25OwnService?: boolean;
  eicrOutsideM25CalendarLink?: string;
  eicrOutsideM25ContactEmail?: string;
  eicrOutsideM25ModalForm?: boolean;

  gasCP12OwnService?: boolean;
  gasCP12CalendarLink?: string;
  gasCP12ContactEmail?: string;
  gasCP12ModalForm?: boolean;

  gasCP12BoilerServiceOwnService?: boolean;
  gasCP12BoilerServiceCalendarLink?: string;
  gasCP12BoilerServiceContactEmail?: string;
  gasCP12BoilerServiceModalForm?: boolean;

  boilerServiceOwnService?: boolean;
  boilerServiceCalendarLink?: string;
  boilerServiceContactEmail?: string;
  boilerServiceModalForm?: boolean;

  floorplanOwnService?: boolean;
  floorplanCalendarLink?: string;
  floorplanContactEmail?: string;
  floorplanModalForm?: boolean;

  epcVesaOwnService?: boolean;
  epcVesaCalendarLink?: string;
  epcVesaContactEmail?: string;
  epcVesaModalForm?: boolean;

  inventoryOwnService?: boolean;
  inventoryCalendarLink?: string;
  inventoryContactEmail?: string;
  inventoryModalForm?: boolean;

  checkoutOwnService?: boolean;
  checkoutCalendarLink?: string;
  checkoutContactEmail?: string;
  checkoutModalForm?: boolean;

  // BUNDLE SERVICE CONFIGURATION FIELDS
  bundleEpcVesaFloorplanOwnService?: boolean;
  bundleEpcVesaFloorplanCalendarLink?: string;
  bundleEpcVesaFloorplanContactEmail?: string;
  bundleEpcVesaFloorplanModalForm?: boolean;

  bundleInventoryCheckInOwnService?: boolean;
  bundleInventoryCheckInCalendarLink?: string;
  bundleInventoryCheckInContactEmail?: string;
  bundleInventoryCheckInModalForm?: boolean;

  bundleEpcVesaLraOwnService?: boolean;
  bundleEpcVesaLraCalendarLink?: string;
  bundleEpcVesaLraContactEmail?: string;
  bundleEpcVesaLraModalForm?: boolean;

  // Sourcing fee configuration
  sourcingFeePercentage?: number;
  sourcingFeeMinimum?: number;
  sourcingFeeVatIncluded?: boolean;

  // widget configuration
  mpgWidgetApiKey?: string|null|undefined;

  // UAE-only (from API when appId is UAE)
  currency?: string;
  valuationLabel?: string;
  valuationSameAsPrice?: boolean;
  strategyLabel?: string;
  useTenureForFilter?: boolean;
}

/** Returns currency symbol/prefix from theme (e.g. "AED " for UAE, "£" otherwise). Uses currency when set; falls back to useTenureForFilter so UAE shows AED even if theme was rehydrated without currency. */
export function getCurrencySymbol(theme: { currency?: string; useTenureForFilter?: boolean } | null | undefined): string {
  if (theme?.currency === "AED") return "AED ";
  if (theme?.useTenureForFilter) return "AED "; // UAE site (same flag as Tenure filter)
  return "£";
}

export const getTheme = async (): Promise<ThemeProps> => {
  try {
    const Authorization = `Bearer ${window.__TENANT__.token}`;
    const response = await axios.get(`${baseUrl}/whitelabel/theme`, {
      headers: { Authorization },
    });
    return response.data.results;
  } catch (error) {
    console.log(error);
    window.location.reload();
    return undefined as any;
  }
};